import { useEffect, useState } from "react";
import Button from "../../components/Button";
import AdminSidebar from "../../components/AdminSidebar";
import {
  Upload,
  Database,
  X,
  FileText,
  Plus,
  Book,
  BookOpen,
  Menu,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  createModel,
  fetchModels,
  fetchCustomModels,
  getKnowledgeBasesWithPromptTemplates,
  getPromptTemplates,
} from "../../../services/modelApi";
import { SearchableDropdown } from "../../components/SearchableDropdown";
import FolderModal from "../../components/FolderModal";
import KnowledgeBaseModal from "../../components/KnowledgeBaseModal";
import { Drawer } from "../../components/ui/Drawer";

function ModelCreation() {
  const [modelHandle, setModelHandle] = useState(undefined);
  const [modelTitle, setModelTitle] = useState("");
  const [modelDescription, setModelDescription] = useState("");
  const [modelFolder, setModelFolder] = useState(null);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [transcriptFiles, setTranscriptFiles] = useState([]); // Changed to array
  const [createLoading, setCreateLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [indexes, setIndexes] = useState([]);
  const [allIndexes, setAllIndexes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [folderSearchLoading, setFolderSearchLoading] = useState(false);
  const [botGreeting, setBotGreeting] = useState("");
  const [internetAccess, setInternetAccess] = useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isKnowledgeBaseModalOpen, setIsKnowledgeBaseModalOpen] =
    useState(false);
  const [conversationStarters, setConversationStarters] = useState([]);
  const [starterInput, setStarterInput] = useState("");

  // Prompt RAG state variables
  const [promptRAGEnabled, setPromptRAGEnabled] = useState(false);
  const [promptStrategy, setPromptStrategy] = useState("enhance");
  const [promptTemplateFiles, setPromptTemplateFiles] = useState([]);
  const [selectedPromptKnowledgeBase, setSelectedPromptKnowledgeBase] =
    useState(null);
  const [promptKnowledgeBases, setPromptKnowledgeBases] = useState([]);
  const [allPromptKnowledgeBases, setAllPromptKnowledgeBases] = useState([]);
  const [
    promptKnowledgeBaseSearchLoading,
    setPromptKnowledgeBaseSearchLoading,
  ] = useState(false);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Validate file types and sizes
    const validFiles = selectedFiles.filter((file) => {
      const validTypes = [".txt", ".pdf", ".json", ".docx"];
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();

      // Check file type
      if (!validTypes.includes(fileExtension)) {
        return false;
      }

      // Check file size (100MB limit)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        toast.error(
          `File "${file.name}" is too large. Maximum file size is 100MB.`
        );
        return false;
      }

      return true;
    });

    if (validFiles.length !== selectedFiles.length) {
      toast.error(
        "Some files were rejected. Only .txt, .pdf, and .json files under 100MB are allowed."
      );
    }

    // Add new files to existing ones (avoiding duplicates)
    setTranscriptFiles((prevFiles) => {
      const existingFileNames = prevFiles.map((f) => f.name);
      const newFiles = validFiles.filter(
        (file) => !existingFileNames.includes(file.name)
      );

      if (newFiles.length === 0 && validFiles.length > 0) {
        toast.info("Some files were already selected.");
      }

      return [...prevFiles, ...newFiles];
    });

    setErrors((p) => ({ ...p, transcript: undefined }));
    // Clear the input to allow selecting the same file again if needed
    e.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    setTranscriptFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const removeAllFiles = () => {
    setTranscriptFiles([]);
  };

  // Prompt template file handlers
  const handlePromptFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const validFiles = selectedFiles.filter((file) => {
      const isValidType =
        file.name.endsWith(".txt") ||
        file.name.endsWith(".json") ||
        file.name.endsWith(".pdf") ||
        file.name.endsWith(".docx");
      const isValidSize = file.size < 10 * 1024 * 1024; // 10MB limit

      if (!isValidType) {
        toast.error(
          `File "${file.name}" must be a .txt, .json, .docx or .pdf file`
        );
        return false;
      }

      if (!isValidSize) {
        toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return false;
      }

      return true;
    });

    setPromptTemplateFiles((prevFiles) => {
      const existingNames = prevFiles.map((f) => f.name);
      const newFiles = validFiles.filter(
        (file) => !existingNames.includes(file.name)
      );

      if (newFiles.length === 0 && validFiles.length > 0) {
        toast.info("Some files were already selected.");
      }

      return [...prevFiles, ...newFiles];
    });

    e.target.value = "";
  };

  const removePromptFile = (indexToRemove) => {
    setPromptTemplateFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const removeAllPromptFiles = () => {
    setPromptTemplateFiles([]);
  };

  const fetchPromptKnowledgeBases = async () => {
    try {
      const res = await getKnowledgeBasesWithPromptTemplates();
      const kbOptions = res.data.map((kb, index) => ({
        label: kb.label, // Use the label from API which includes the prompt namespace name
        value: kb.value, // Use the value from API (original kb name for API calls)
        id: index + 1,
        templateCount: kb.templateCount,
        promptNamespace: kb.promptNamespace,
        originalKbName: kb.originalKbName,
      }));
      setPromptKnowledgeBases(kbOptions);
      setAllPromptKnowledgeBases(kbOptions);
    } catch (error) {
      console.error("Error fetching prompt knowledge bases:", error);
      setPromptKnowledgeBases([]);
      setAllPromptKnowledgeBases([]);
    }
  };

  const handlePromptKnowledgeBaseSearch = (query) => {
    setPromptKnowledgeBaseSearchLoading(true);
    setTimeout(() => {
      const allOptions = [...allPromptKnowledgeBases];
      if (query) {
        const filtered = allOptions.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );
        setPromptKnowledgeBases(filtered);
      } else {
        setPromptKnowledgeBases(allOptions);
      }
      setPromptKnowledgeBaseSearchLoading(false);
    }, 500);
  };

  useEffect(() => {
    fetchHandles();
    fetchFolders();
    fetchPromptKnowledgeBases();
  }, []);

  const handleCreateModel = async () => {
    try {
      setCreateLoading(true);

      // Clear previous errors
      setErrors({});

      // Validate required fields
      const newErrors = {};

      if (!modelTitle || modelTitle.trim() === "") {
        newErrors.modelTitle = "Bot name is required";
      }

      if (!modelHandle) {
        newErrors.model = "Please select a knowledge base";
      }

      if (transcriptFiles.length === 0) {
        newErrors.transcript = "At least one transcript file is required";
      }

      // If there are validation errors, show them and return
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error("Please fill in all required fields");
        setCreateLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("title", modelTitle);
      formData.append("description", modelDescription);
      formData.append("folder", modelFolder?.value || "");
      formData.append("systemPrompt", systemPrompt);

      // Add greeting if provided
      if (botGreeting.trim()) {
        formData.append("greeting", botGreeting.trim());
      }

      // Append all files with the same field name that matches backend expectation
      transcriptFiles.forEach((file) => {
        formData.append("transcriptFiles", file); // Changed to match backend route
      });

      formData.append("modelHandle", modelHandle.value);
      formData.append("conversationStarters", conversationStarters);
      formData.append("internetAccess", internetAccess);

      // Add prompt RAG configuration
      formData.append("promptRAGEnabled", promptRAGEnabled);
      if (promptRAGEnabled) {
        formData.append("promptStrategy", promptStrategy);

        // Append prompt template files
        promptTemplateFiles.forEach((file) => {
          formData.append("promptTemplateFiles", file);
        });

        // Add selected existing prompt knowledge base
        if (selectedPromptKnowledgeBase) {
          formData.append(
            "existingPromptKnowledgeBase",
            selectedPromptKnowledgeBase.value
          );
        }
      }

      await createModel(formData);
      toast.success(
        `Bot created successfully with ${
          transcriptFiles.length
        } knowledge files${
          promptTemplateFiles.length > 0
            ? ` and ${promptTemplateFiles.length} prompt templates`
            : ""
        }!`
      );

      // Reset form
      setModelTitle("");
      setModelDescription("");
      setModelFolder(null);
      setSystemPrompt("");
      setModelHandle(undefined);
      setTranscriptFiles([]);
      setBotGreeting("");
      setConversationStarters([]);
      setStarterInput("");
      setInternetAccess(false);
      // Reset prompt RAG fields
      setPromptRAGEnabled(false);
      setPromptStrategy("enhance");
      setPromptTemplateFiles([]);
      setSelectedPromptKnowledgeBase(null);
    } catch (error) {
      toast.error(error.message || "Failed to create bot");
    } finally {
      setCreateLoading(false);
    }
  };

  const fetchHandles = async () => {
    try {
      const res = await fetchModels();
      console.log("indexes", res);
      setIndexes(
        res.data.map((item, i) => ({
          label: item.name,
          value: item.name,
          id: i + 1,
        }))
      );
      setAllIndexes(
        res.data.map((item, i) => ({
          label: item.name,
          value: item.name,
          id: i + 1,
        }))
      );
    } catch (error) {
      toast.error(error);
    }
  };

  const fetchFolders = async () => {
    try {
      // Fetch existing models to get their folder information
      const res = await fetchCustomModels();

      // Extract unique folders from existing models
      const existingFolders = new Set();

      // Add folders from existing models
      res.data.forEach((model) => {
        if (model.folder && model.folder.trim() !== "") {
          existingFolders.add(model.folder);
        }
      });

      // Convert to folder objects
      const allFolders = Array.from(existingFolders).map(
        (folderValue, index) => {
          // For custom folders, create a label from the value
          const label = folderValue
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          return {
            label: label,
            value: folderValue,
            id: index + 1,
          };
        }
      );

      setFolders(allFolders);
      setAllFolders(allFolders);
    } catch (error) {
      console.error("Error fetching folders from models:", error);
      // Fallback to empty array if fetching fails
      setFolders([]);
      setAllFolders([]);
    }
  };

  const handleSearch = (query) => {
    // Simulate API call
    setTimeout(() => {
      const allOptions = [...allIndexes];
      if (query) {
        const filtered = allOptions.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );
        setIndexes(filtered);
      } else {
        setIndexes(allOptions);
      }
    }, 500);
  };

  const handleFolderSearch = (query) => {
    setFolderSearchLoading(true);
    // Simulate API call
    setTimeout(() => {
      const allOptions = [...allFolders];
      if (query) {
        const filtered = allOptions.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );
        setFolders(filtered);
      } else {
        setFolders(allOptions);
      }
      setFolderSearchLoading(false);
    }, 500);
  };

  const handleFolderModalSave = async (folderData) => {
    try {
      // Create new folder object
      const newFolder = {
        label: folderData.name,
        value: folderData.name.toLowerCase().replace(/\s+/g, "-"),
        id: allFolders.length + 1,
      };

      // Add to the current folders list
      const updatedFolders = [...allFolders, newFolder];

      // Update the state
      setFolders(updatedFolders);
      setAllFolders(updatedFolders);
      setModelFolder(newFolder);
      toast.success("Folder created successfully");
    } catch (error) {
      console.error("Error creating folder:", error);
      toast.error("Failed to create folder");
      throw error;
    }
  };

  const handleKnowledgeBaseModalSave = async (knowledgeBaseData) => {
    try {
      // Create new knowledge base object
      const newKnowledgeBase = {
        label: knowledgeBaseData.name,
        value: knowledgeBaseData.name,
        id: indexes.length + 1,
      };

      // Add to the current indexes list
      const updatedIndexes = [...indexes, newKnowledgeBase];
      const updatedAllIndexes = [...allIndexes, newKnowledgeBase];

      // Update the state
      setIndexes(updatedIndexes);
      setAllIndexes(updatedAllIndexes);
      setModelHandle(newKnowledgeBase);
      toast.success("Knowledge base created successfully");
    } catch (error) {
      console.error("Error creating knowledge base:", error);
      toast.error("Failed to create knowledge base");
      throw error;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };
  const [showSidebar, setShowSidebar] = useState(false);
  const onClose = () => {
    setShowSidebar(false);
  };

  return (
    <div className="w-screen  bg-gray-800 overflow-hidden">
      <div className="flex relative  overflow-hidden">
        {/* Sidebar */}
        <div className="hidden sticky left-0 bottom-0 lg:block w-64 h-full">
          <AdminSidebar />
        </div>
        <Drawer
          height="full"
          title={"Admin"}
          isOpen={showSidebar}
          onClose={onClose}
        >
          <div className=" lg:hidden  w-full h-full">
            <AdminSidebar />
          </div>
        </Drawer>
        {/* Main Content */}
        <div className="flex-1 flex flex-col  h-full overflow-y-auto bg-gray-800">
          {/* Header */}
          <div className="px-3 lg:px-8  flex justify-between items-start py-6 border-b border-gray-700">
            <div className="flex items-center w-[90%] lg:w-full gap-3">
              <Database className="text-blue-400" size={24} />
              <div>
                <h1 className="text-2xl font-semibold text-white">
                  Create AI Bot
                </h1>
                <p className="text-gray-400 text-sm">
                  Configure bot details and upload knowledge base
                </p>
              </div>
            </div>
                 <div
              onClick={() => {
                setShowSidebar(true);
              }}
              className="max-w-max lg:hidden"
            >
              <Menu size={30} color="white" />
            </div>
          </div>

          {/* Form Content */}
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4  flex-1 p-2 lg:p-8 ">
            {/* border border-gray-700 */}
            <div className="  mx-auto bg-gray-900 border border-gray-700 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-8">
                <Database className="text-blue-400" size={24} />
                <h2 className="text-lg font-semibold text-white">
                  Bot Configuration
                </h2>
              </div>

              {/* Required Fields Summary */}
              <div className="mb-6 p-4 bg-blue-900 bg-opacity-20 border border-blue-600 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-blue-300 flex items-center gap-2">
                    <span>ℹ</span>
                    Required Fields
                  </h3>
                  <div className="text-xs text-blue-200">
                    {(() => {
                      const completed = [
                        modelTitle.trim() !== "",
                        !!modelHandle,
                        transcriptFiles.length > 0,
                      ].filter(Boolean).length;
                      const total = 3;
                      return `${completed}/${total} completed`;
                    })()}
                  </div>
                </div>
                <ul className="text-xs text-blue-200 space-y-1">
                  <li
                    className={`flex items-center gap-2 ${
                      modelTitle.trim() !== "" ? "text-green-300" : ""
                    }`}
                  >
                    <span>{modelTitle.trim() !== "" ? "✓" : "•"}</span>
                    <strong>Name:</strong> The name of your AI bot
                  </li>
                  <li
                    className={`flex items-center gap-2 ${
                      modelHandle ? "text-green-300" : ""
                    }`}
                  >
                    <span>{modelHandle ? "✓" : "•"}</span>
                    <strong>Knowledge Base:</strong> Select or create a
                    knowledge base for your bot
                  </li>
                  <li
                    className={`flex items-center gap-2 ${
                      transcriptFiles.length > 0 ? "text-green-300" : ""
                    }`}
                  >
                    <span>{transcriptFiles.length > 0 ? "✓" : "•"}</span>
                    <strong>Knowledge Base Files:</strong> Upload at least one
                    transcript file (.txt or .pdf or .json)
                  </li>
                </ul>
                <div className="mt-3 pt-3 border-t border-blue-600/30">
                  <p className="text-xs text-purple-300">
                    <strong>Optional:</strong> Enable Prompt Optimization to
                    enhance AI responses with contextual prompt templates
                    {promptRAGEnabled && (
                      <span className="ml-2 px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs">
                        {promptStrategy} strategy • {promptTemplateFiles.length}{" "}
                        templates
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-800 text-white placeholder:text-gray-400 ${
                    errors.modelTitle
                      ? "border-red-500 focus:ring-red-500"
                      : modelTitle.trim() === ""
                      ? "border-yellow-500"
                      : "border-gray-600"
                  }`}
                  value={modelTitle}
                  onChange={(e) => {
                    setModelTitle(e.target.value);
                    setErrors((p) => ({ ...p, modelTitle: undefined }));
                  }}
                  placeholder="Enter bot name (required)"
                  disabled={createLoading}
                />
                {errors.modelTitle && (
                  <small className="mt-1 text-red-500 flex items-center gap-1">
                    <span>⚠</span>
                    {errors.modelTitle}
                  </small>
                )}
                {!errors.modelTitle && modelTitle.trim() === "" && (
                  <small className="mt-1 text-yellow-500 flex items-center gap-1">
                    <span>ℹ</span>
                    This field is required
                  </small>
                )}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Description
                </label>
                <textarea
                  className="w-full border border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-800 text-white placeholder:text-gray-400 resize-none"
                  value={modelDescription}
                  onChange={(e) => {
                    setModelDescription(e.target.value);
                    setErrors((p) => ({ ...p, modelDescription: undefined }));
                  }}
                  placeholder="Enter bot description"
                  disabled={createLoading}
                  rows={3}
                />
                {errors.modelDescription && (
                  <small className="mt-1 text-red-600">
                    {errors.modelDescription}
                  </small>
                )}
              </div>

              <div className="mb-6 ">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Folder
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SearchableDropdown
                      showAddButton={false}
                      className="w-full"
                      valueClassName="border border-gray-600 px-3 py-2 w-full bg-gray-800 text-white"
                      value={modelFolder}
                      onChange={(value) => {
                        setModelFolder(value);
                        setErrors((p) => ({ ...p, modelFolder: undefined }));
                      }}
                      onSearch={handleFolderSearch}
                      options={folders}
                      loading={folderSearchLoading}
                      placeholder="Select a folder..."
                      searchPlaceholder="Search folders..."
                      theme="dark"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsFolderModalOpen(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    New Folder
                  </button>
                </div>
                {errors.modelFolder && (
                  <small className="mt-1 text-red-600">
                    {errors.modelFolder}
                  </small>
                )}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Prompt
                </label>
                <textarea
                  className="w-full border border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-800 text-white placeholder:text-gray-400 resize-none"
                  value={systemPrompt}
                  onChange={(e) => {
                    setSystemPrompt(e.target.value);
                    setErrors((p) => ({ ...p, systemPrompt: undefined }));
                  }}
                  placeholder="Enter system instruction prompt"
                  disabled={createLoading}
                  rows={4}
                />
                {errors.systemPrompt && (
                  <small className="mt-1 text-red-600">
                    {errors.systemPrompt}
                  </small>
                )}
              </div>
            </div>
            {/* second part  */}
            <div className="  mx-auto bg-gray-900 border border-gray-700 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-8">
                <BookOpen className="text-blue-400" size={24} />
                <h2 className="text-lg font-semibold text-white">
                  Knowledgebase Configuration
                </h2>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Knowledge Base <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SearchableDropdown
                      showAddButton={false}
                      className="w-full"
                      valueClassName={`px-3 py-2 w-full bg-gray-800 text-white ${
                        errors.model
                          ? "border border-red-500"
                          : !modelHandle
                          ? "border border-yellow-500"
                          : "border border-gray-600"
                      }`}
                      value={modelHandle}
                      onChange={(value) => {
                        setModelHandle(value);
                        setErrors((p) => ({ ...p, model: undefined }));
                      }}
                      onSearch={handleSearch}
                      options={indexes}
                      loading={false}
                      placeholder="Select a knowledge base (required)..."
                      searchPlaceholder="Search knowledge bases..."
                      theme="dark"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsKnowledgeBaseModalOpen(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    New
                  </button>
                </div>
                {errors.model && (
                  <small className="mt-1 text-red-500 flex items-center gap-1">
                    <span>⚠</span>
                    {errors.model}
                  </small>
                )}
                {!errors.model && !modelHandle && (
                  <small className="mt-1 text-yellow-500 flex items-center gap-1">
                    <span>ℹ</span>
                    Please select a knowledge base
                  </small>
                )}
              </div>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-300">
                    Knowledge Base Files <span className="text-red-500">*</span>
                  </label>
                  {transcriptFiles.length > 0 && (
                    <button
                      type="button"
                      onClick={removeAllFiles}
                      className="text-xs text-red-400 hover:text-red-300 font-medium"
                      disabled={createLoading}
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {/* File Upload Button */}
                <div className="flex items-center gap-3 ">
                  <label className="flex gap-2 w-full items-center justify-center cursor-pointer px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700 transition">
                    <Upload className="text-blue-400" size={20} />
                    <span className="text-sm font-medium text-gray-300">
                      {transcriptFiles.length === 0
                        ? "Choose Files"
                        : "Add More Files"}
                    </span>
                    <input
                      type="file"
                      accept=".txt,.pdf,.json,.docx"
                      className="hidden"
                      onChange={handleFileChange}
                      disabled={createLoading}
                      multiple
                    />
                  </label>

                  {transcriptFiles.length > 0 && (
                    <span className="text-sm text-blue-400 font-medium">
                      {transcriptFiles.length} file
                      {transcriptFiles.length !== 1 ? "s" : ""} selected
                    </span>
                  )}
                </div>

                <div className="mt-2 text-xs text-gray-400">
                  <span>
                    Maximum file size: 100MB per file. Supported formats: .txt,
                    .pdf, .json, .docx
                  </span>
                </div>

                <div>
                  {errors.transcript && (
                    <small className="text-red-500 flex items-center gap-1">
                      <span>⚠</span>
                      {errors.transcript}
                    </small>
                  )}
                  {!errors.transcript && transcriptFiles.length === 0 && (
                    <small className="text-yellow-500 flex items-center gap-1">
                      <span>ℹ</span>
                      At least one transcript file is required
                    </small>
                  )}
                </div>

                {/* Selected Files List */}
                {transcriptFiles.length > 0 && (
                  <div className="bg-gray-800 border border-gray-600 rounded-lg p-4 max-h-48 overflow-y-auto">
                    <h4 className="text-sm font-semibold text-gray-300 mb-3">
                      Selected Files:
                    </h4>

                    <div className="space-y-2">
                      {transcriptFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gray-700 p-3 rounded border border-gray-600"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText
                              className="text-blue-400 flex-shrink-0"
                              size={16}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-400">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-red-400 hover:text-red-300 p-1 rounded-full hover:bg-red-500/20 transition-colors flex-shrink-0"
                            disabled={createLoading}
                            title="Remove file"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Conversation starter message Section */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Conversation Starters
                </label>
                <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="flex-1 px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Add a starter, e.g., “What can you do?”"
                      value={starterInput}
                      onChange={(e) => setStarterInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && starterInput.trim()) {
                          setConversationStarters((prev) => [
                            ...prev,
                            starterInput.trim(),
                          ]);
                          setStarterInput("");
                        }
                      }}
                      disabled={createLoading}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (starterInput.trim()) {
                          setConversationStarters((prev) => [
                            ...prev,
                            starterInput.trim(),
                          ]);
                          setStarterInput("");
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      disabled={createLoading}
                    >
                      Add
                    </button>
                  </div>

                  {conversationStarters?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {conversationStarters?.map((s, i) => (
                        <span
                          key={`${s}-${i}`}
                          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-700 text-gray-100 text-xs border border-gray-600"
                        >
                          {s}
                          <button
                            type="button"
                            className="text-red-300 hover:text-red-200"
                            onClick={() =>
                              setConversationStarters((prev) =>
                                prev.filter((_, idx) => idx !== i)
                              )
                            }
                            disabled={createLoading}
                            title="Remove starter"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {conversationStarters?.length === 0 && (
                    <p className="mt-2 text-xs text-gray-400">
                      Add short prompts to help users start the conversation.
                      You can add multiple.
                    </p>
                  )}
                </div>
              </div>

              {/* Internet Access Toggle */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-300">
                    Internet Access
                  </label>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        internetAccess
                          ? "bg-green-500/20 text-green-400"
                          : "bg-red-500/20 text-red-400"
                      }`}
                    >
                      {internetAccess ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-400 mb-3">
                  When enabled, this bot can access the internet to search for
                  current information and browse websites. When disabled, the
                  bot will only use its knowledge base and cannot access
                  external information.
                </p>

                <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Allow Internet Access
                      </label>
                      <p className="text-xs text-gray-400">
                        {internetAccess
                          ? "Bot can search the web and access current information"
                          : "Bot is restricted to knowledge base only"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setInternetAccess(!internetAccess)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                        internetAccess ? "bg-blue-600" : "bg-gray-600"
                      }`}
                      disabled={createLoading}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          internetAccess ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Prompt RAG Configuration */}
              <div className="mb-6">
                <div className="flex flex-col lg:flex-row items-start gap-2 lg:items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-gray-300">
                    Prompt Optimization (Beta)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={promptRAGEnabled}
                      onChange={(e) => setPromptRAGEnabled(e.target.checked)}
                      className="rounded"
                      disabled={createLoading}
                    />
                    <span className="text-sm text-gray-400">
                      Enable prompt optimization
                    </span>
                  </div>
                </div>

                <div className="text-xs text-gray-400 mb-3">
                  Upload prompt templates to optimize AI responses based on
                  context and query patterns. This feature enhances response
                  quality by using contextual prompt selection.
                </div>

                {promptRAGEnabled && (
                  <>
                    {/* Strategy Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Optimization Strategy
                      </label>
                      <select
                        value={promptStrategy}
                        onChange={(e) => setPromptStrategy(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                        disabled={createLoading}
                      >
                        <option value="enhance">
                          Enhance - Add context to existing prompt
                        </option>
                        <option value="optimize">
                          Optimize - Use template-based prompting
                        </option>
                        <option value="hybrid">
                          Hybrid - Combine templates with base prompt
                        </option>
                      </select>
                      <div className="text-xs text-gray-400 mt-1">
                        {promptStrategy === "enhance" &&
                          "Adds contextual information to your existing system prompt"}
                        {promptStrategy === "optimize" &&
                          "Replaces system prompt with optimized templates based on query"}
                        {promptStrategy === "hybrid" &&
                          "Intelligently merges templates with your base prompt"}
                      </div>
                    </div>

                    {/* Existing Prompt Knowledge Base Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Select Existing Prompt Templates (Optional)
                      </label>
                      <SearchableDropdown
                        showAddButton={false}
                        className="w-full"
                        valueClassName="border border-purple-600 px-3 py-2 w-full bg-gray-800 text-white"
                        value={selectedPromptKnowledgeBase}
                        onChange={(value) => {
                          setSelectedPromptKnowledgeBase(value);
                        }}
                        onSearch={handlePromptKnowledgeBaseSearch}
                        options={promptKnowledgeBases}
                        loading={promptKnowledgeBaseSearchLoading}
                        placeholder="Select existing prompt templates..."
                        searchPlaceholder="Search prompt knowledge bases..."
                        theme="dark"
                      />
                      <div className="mt-2 text-xs text-gray-400">
                        Choose from existing knowledge bases that contain prompt
                        templates, or upload new ones below.
                      </div>
                    </div>

                    {/* Prompt Template Upload */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-300">
                          Prompt Templates
                        </label>
                        {promptTemplateFiles.length > 0 && (
                          <button
                            type="button"
                            onClick={removeAllPromptFiles}
                            className="text-xs text-red-400 hover:text-red-300 font-medium"
                            disabled={createLoading}
                          >
                            Clear All
                          </button>
                        )}
                      </div>

                      <label className="flex gap-2 w-full items-center justify-center cursor-pointer px-4 py-3 bg-gray-800 border border-purple-600 rounded-lg hover:bg-gray-700 transition">
                        <Upload className="text-purple-400" size={20} />
                        <span className="text-sm font-medium text-gray-300">
                          {promptTemplateFiles.length === 0
                            ? "Upload Prompt Templates (.txt, .json, .pdf, .docx)"
                            : "Add More Templates"}
                        </span>
                        <input
                          type="file"
                          accept=".txt,.json,.pdf,.docx"
                          multiple
                          className="hidden"
                          onChange={handlePromptFileChange}
                          disabled={createLoading}
                        />
                      </label>

                      <div className="mt-2 text-xs text-gray-400">
                        <span>
                          Maximum file size: 10MB per file. Supported formats:
                          .txt, .json, .pdf, .docx
                        </span>
                      </div>

                      {promptTemplateFiles.length > 0 && (
                        <>
                          <span className="text-sm text-purple-400 font-medium mt-2 block">
                            {promptTemplateFiles.length} template
                            {promptTemplateFiles.length !== 1 ? "s" : ""}{" "}
                            selected
                          </span>

                          {/* Selected Template Files */}
                          <div className="bg-gray-800 border border-purple-600 rounded-lg p-4 max-h-32 overflow-y-auto mt-3">
                            <h4 className="text-sm font-semibold text-gray-300 mb-3">
                              Prompt Templates:
                            </h4>
                            <div className="space-y-2">
                              {promptTemplateFiles.map((file, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between bg-gray-700 p-2 rounded border border-gray-600"
                                >
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <FileText
                                      className="text-purple-400 flex-shrink-0"
                                      size={14}
                                    />
                                    <span className="text-sm text-white truncate">
                                      {file.name}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removePromptFile(index)}
                                    className="text-red-400 hover:text-red-300 p-1"
                                    disabled={createLoading}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Info Box */}
                    <div className="bg-purple-900/20 border border-purple-600/30 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-purple-300 mb-1">
                            How Prompt Templates Work
                          </h3>
                          <ul className="text-xs text-gray-300 space-y-1">
                            <li>
                              • <strong>System templates:</strong> Replace or
                              enhance your base system prompt
                            </li>
                            <li>
                              • <strong>Context templates:</strong> Improve how
                              the AI processes retrieved information
                            </li>
                            <li>
                              • <strong>User templates:</strong> Enhance user
                              interaction patterns
                            </li>
                            <li>
                              • Templates are automatically categorized based on
                              content
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <Button
                name="Create Bot"
                onClick={handleCreateModel}
                isLoading={createLoading}
                type="secondary"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
                disabled={
                  createLoading ||
                  !modelTitle ||
                  !modelHandle ||
                  transcriptFiles.length === 0
                }
                icon={<Database size={20} />}
              />
            </div>
          </div>
        </div>
      </div>
      {isFolderModalOpen && (
        <FolderModal
          isOpen={isFolderModalOpen}
          onClose={() => setIsFolderModalOpen(false)}
          onSave={handleFolderModalSave}
          initialFolderName={""} // Pass an empty string for new folder
        />
      )}
      {isKnowledgeBaseModalOpen && (
        <KnowledgeBaseModal
          isOpen={isKnowledgeBaseModalOpen}
          onClose={() => setIsKnowledgeBaseModalOpen(false)}
          onSave={handleKnowledgeBaseModalSave}
          initialKnowledgeBaseName={""}
        />
      )}
    </div>
  );
}

export default ModelCreation;
